import { Inject } from '@odinjs/odin';
import { ActionAPI, BaseModalProps, ButtonAPI, DeckAPI, ModalButton, ModalService, PanelAPI } from '@philips-internal/oswald-ui-components';
import Controller, { OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { Value } from '@philips-internal/oswald-ui-core';
import { AbortableCloseEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { ModalButtonType, ModalType } from '@philips-internal/oswald-ui-constants';
import { FragmentNames, ReservedBloodProductsViewerAliases } from '../../../../../codegen';
import BaseController from '../../../../BaseController';
import InformationBarUtils from '../../../../commons/header/information-bar-header-utils';
import ReservedBloodProductsContext from '../reserved-blood-products-service';
import { ActionType, InfusionItemStatus } from '../../../../../constants/infusion-solutions';
import { AdminByScheduleParams } from '../../administration-schedule/administration-schedule-params';

@Controller({ component: ReservedBloodProductsViewerAliases.RESERVED_BLOOD_PRODUCTS_VIEWER_LAYOUT, fragment: FragmentNames.RESERVED_BLOOD_PRODUCTS_VIEWER })
export default class ReservedBloodProductsVIewerController extends BaseController {

  @Inject(ReservedBloodProductsViewerAliases.RESERVED_BLOOD_PRODUCTS_VIEWER_CONTENT_PANEL)
  private contentPanelAPI!: PanelAPI

  @Inject(ReservedBloodProductsViewerAliases.START_BUTTON)
  private startButtonAPI!: ButtonAPI;

  @Inject(ReservedBloodProductsViewerAliases.INTERRUPT_BUTTON)
  private interruptButtonAPI!: ButtonAPI;

  @Inject(ReservedBloodProductsViewerAliases.RESERVED_BLOOD_PRODUCTS_VIEWER_FOOTER_PANEL)
  private buttonsPanelAPI!: PanelAPI;

  @OnMount(ReservedBloodProductsViewerAliases.RESERVED_BLOOD_PRODUCTS_VIEWER_LAYOUT)
  handleMountPanelCard(): void {
    InformationBarUtils.collapsedBar = true;
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    this.context.set({ encounterId, patientId })
    this.contentPanelAPI.setVisible(true);
  }

  @OnMount(ReservedBloodProductsViewerAliases.RESERVED_BLOOD_PRODUCTS_VIEWER_FOOTER_PANEL)
  handleFooterPanelButtonsMount(): void {
    this.buttonsPanelAPI.setVisible(true);
  }

  @OnMount(ReservedBloodProductsViewerAliases.RESERVED_BLOOD_PRODUCTS_VIEWER_DECK)
  handleBloodManagementMount({ target }: EventData<DeckAPI>): void {
    const reservedBloodProducts = ReservedBloodProductsContext.get()
    target.setData([{
      bloodNumber: reservedBloodProducts.bagNumber,
      currentSpeedInfusion: reservedBloodProducts.infusionSpeed,
      description: reservedBloodProducts.description,
      expirationDate: reservedBloodProducts.endDate,
      isChemotherapy: false,
      reservationDate: reservedBloodProducts.reservationDate,
      reservationNumber: reservedBloodProducts.reservationId,
      statusItem: reservedBloodProducts.statusItem,
      statusItemDescription: reservedBloodProducts.status,
      totalVolume: reservedBloodProducts.volume,
      unitMeasurement: reservedBloodProducts.unitMeasurement
    }])
  }

  @OnClick(ReservedBloodProductsViewerAliases.START_BUTTON)
  handleClickExecuteTimeButton(): void {
    const { statusItem } = this.historyService.getCurrentRouteParams() as { statusItem: string };

    if (this.shouldRestartBlood(statusItem)) {
      const restartDialogModal = this.createDialogModal(FragmentNames.RESERVED_BLOOD_PRODUCTS_RESTART, 'button.infusion-viewer.restart');
      ModalService.openDefault({ ...restartDialogModal  });
      return
    }

    const startDialogModal = this.createDialogModal(FragmentNames.RESERVED_BLOOD_PRODUCTS_START, 'text.emar-administration');
    ModalService.openDefault({ ...startDialogModal });
  }

  private createDialogModal(fragment: string, dialogTitle: string): BaseModalProps {
    const reservedProduct = ReservedBloodProductsContext.get() as unknown as Record<string, Value>;
    return {
      contextData: reservedProduct,
      focusOnOpen: false,
      fragment,
      id: fragment,
      onBeforeClose: (event) => this.dialogModalConfirm(event, fragment),
      title: MessageService.translate(dialogTitle),
      type: ModalType.DEFAULT
    };
  }

  @OnMount(ReservedBloodProductsViewerAliases.START_BUTTON)
  handleStartButtonMount(): void {
    this.validateAndSetButtonVisibility();
  }

  @OnClick(ReservedBloodProductsViewerAliases.INTERRUPT_BUTTON)
  handleInterruptButtonClick(): void {
    const interruptExpression = 'button.infusion-viewer.interrupt';
    const interruptDescription = MessageService.translate(interruptExpression);
    const reservedProduct = ReservedBloodProductsContext.get() as unknown as Record<string, Value>;
    const dialogModal: BaseModalProps = {
      contextData: reservedProduct,
      focusOnOpen: false,
      fragment: FragmentNames.RESERVED_BLOOD_PRODUCTS_INTERRUPT,
      id: FragmentNames.RESERVED_BLOOD_PRODUCTS_INTERRUPT,
      onBeforeClose: (event) => this.dialogModalConfirm(event, FragmentNames.RESERVED_BLOOD_PRODUCTS_INTERRUPT),
      title: interruptDescription,
      type: ModalType.DEFAULT,
    };

    ModalService.openDefault(dialogModal);
  }

  @OnMount(ReservedBloodProductsViewerAliases.INTERRUPT_BUTTON)
  handleInteruptButtonMount(): void {
    const { statusItem } = this.historyService.getCurrentRouteParams() as { statusItem: string };
    const isValidItemStatusForInterrupt = [InfusionItemStatus.STARTED, InfusionItemStatus.RESTARTED].some(status => status === statusItem);
    this.interruptButtonAPI.setVisible(isValidItemStatusForInterrupt);
  }

  @OnMount(ReservedBloodProductsViewerAliases.CONTEXTUAL_BAR_ACTION_TERMINATE)
  handleMountTerminateAction(event: MountEvent): void {
    const actionApi = event.target as ActionAPI
    const { statusItem } = this.historyService.getCurrentRouteParams() as { statusItem: string };
    const isValidItemStatusForInterrupt = [InfusionItemStatus.STARTED, InfusionItemStatus.RESTARTED, InfusionItemStatus.INTERRUPTED].some(status => status === statusItem);
    actionApi.setEnabled(isValidItemStatusForInterrupt);
  }

  @OnClick(ReservedBloodProductsViewerAliases.CONTEXTUAL_BAR_ACTION_TERMINATE)
  handleClickTerminateAction(): void {
    const terminateExpression = 'text.item-terminate';
    const terminateDescription = MessageService.translate(terminateExpression);
    const reservedProduct = ReservedBloodProductsContext.get() as unknown as Record<string, Value>;
    const dialogModal: BaseModalProps = {
      contextData: reservedProduct,
      focusOnOpen: false,
      fragment: FragmentNames.RESERVED_BLOOD_PRODUCTS_TERMINATE,
      id: FragmentNames.RESERVED_BLOOD_PRODUCTS_TERMINATE,
      onBeforeClose: (event) => this.dialogModalConfirm(event, FragmentNames.RESERVED_BLOOD_PRODUCTS_TERMINATE),
      title: terminateDescription,
      type: ModalType.DEFAULT,
    };

    ModalService.openDefault(dialogModal);
  }

  @OnMount(ReservedBloodProductsViewerAliases.CONTEXTUAL_BAR_ACTION_REVERSE)
  handleMountReverseAction(event: MountEvent): void {
    const actionApi = event.target as ActionAPI
    const { statusItem } = this.historyService.getCurrentRouteParams() as { statusItem: string };
    const isValidItemStatusForReverse = [InfusionItemStatus.STARTED, InfusionItemStatus.RESTARTED, InfusionItemStatus.INTERRUPTED].some(status => status === statusItem);
    actionApi.setEnabled(isValidItemStatusForReverse);
  }

  @OnClick(ReservedBloodProductsViewerAliases.CONTEXTUAL_BAR_ACTION_REVERSE)
  handleClicktReverseAction(): void {
    const reverseExpression = 'text.revert-last-event';
    const reverseDescription = MessageService.translate(reverseExpression);
    const reservedProduct = ReservedBloodProductsContext.get() as unknown as Record<string, Value>;
    const dialogModal: BaseModalProps = {
      contextData: reservedProduct,
      focusOnOpen: false,
      fragment: FragmentNames.RESERVED_BLOOD_PRODUCTS_REVERSE,
      id: FragmentNames.RESERVED_BLOOD_PRODUCTS_REVERSE,
      onBeforeClose: (event) => this.dialogModalConfirm(event, FragmentNames.RESERVED_BLOOD_PRODUCTS_REVERSE),
      title: reverseDescription,
      type: ModalType.DEFAULT,
    };

    ModalService.openDefault(dialogModal);

  }
  @OnMount(ReservedBloodProductsViewerAliases.CONTEXTUAL_BAR_ACTION_SUSPEND)
  handleMountSuspendAction(event: MountEvent): void {
    const actionApi = event.target as ActionAPI
    const { statusItem } = this.historyService.getCurrentRouteParams() as { statusItem: string };
    actionApi.setEnabled(InfusionItemStatus.NOT_STARTED === statusItem);
  }

  @OnClick(ReservedBloodProductsViewerAliases.CONTEXTUAL_BAR_ACTION_SUSPEND)
  handleClickSuspendAction(): void {
    const suspendExpression = 'label.suspend-medication.title';
    const suspendDescription = MessageService.translate(suspendExpression);
    const reservedProduct = ReservedBloodProductsContext.get() as unknown as Record<string, Value>;
    const dialogModal: BaseModalProps = {
      contextData: reservedProduct,
      focusOnOpen: false,
      fragment: FragmentNames.RESERVED_BLOOD_PRODUCTS_SUSPEND,
      id: FragmentNames.RESERVED_BLOOD_PRODUCTS_SUSPEND,
      onBeforeClose: (event) => this.dialogModalConfirm(event, FragmentNames.RESERVED_BLOOD_PRODUCTS_SUSPEND),
      title: suspendDescription,
      type: ModalType.DEFAULT,
    };

    ModalService.openDefault(dialogModal);
  }

  private validateAndSetButtonVisibility(): void {
    const { statusItem } = this.historyService.getCurrentRouteParams() as { statusItem: string };

    if (this.shouldStartBlood(statusItem)) {
      const label = this.setScreenLabel(ActionType.START_SOLUTION);
      this.setExecuteTimeButton(label);
      return;
    }

    if (this.shouldRestartBlood(statusItem)) {
      const label = this.setScreenLabel(ActionType.RESTART_SOLUTION);
      this.setExecuteTimeButton(label);
      return;
    }

    this.startButtonAPI.setVisible(false);
  }

  private shouldStartBlood(statusItem: string): boolean {
    return statusItem === InfusionItemStatus.NOT_STARTED;
  }

  private shouldRestartBlood(statusItem: string): boolean {
    const isInterrupted = this.isInterrupted(statusItem);
    const isPending = statusItem === InfusionItemStatus.INTERRUPTED;
    return isInterrupted || isPending;
  }

  private setExecuteTimeButton(label: string): void {
    this.startButtonAPI.setValue(MessageService.translate(label));
    this.startButtonAPI.setVisible(true);
  }

  private setScreenLabel(actionType: string): string {
    const { statusItem } = this.historyService.getCurrentRouteParams() as { statusItem: string };
    const isInterrupted = this.isInterrupted(statusItem)

    if (this.validateRestartButton(actionType, isInterrupted, statusItem)) {
      return 'txt.restart-transfusion'
    }

    if (actionType === ActionType.START_SOLUTION) {
      return 'txt.start-transfusion'
    }

    return ''
  }

  private isInterrupted(statusItem: string): boolean {
    return [InfusionItemStatus.II.valueOf(), InfusionItemStatus.INTERRUPTED.valueOf()].includes(statusItem)
  }

  validateRestartButton(actionType: string, isInterrupted: boolean, statusItem: string): boolean {
    return actionType !== ActionType.START_SOLUTION ||
      (isInterrupted) ||
      (statusItem === InfusionItemStatus.INTERRUPTED);
  }

  private dialogModalConfirm(event: AbortableCloseEvent, id: string): void {
    const noCardsRead = AdminByScheduleParams.readParam?.noCardsRead;

    if (noCardsRead) {
      ModalService.close(id);
      ModalService.close('dialog-modal-service');
      return;
    }

    event.abort();

    const buttons: ModalButton[] = [];

    buttons.push({
      action: () => { },
      title: 'tws.biometry.cancel',
      type: ModalButtonType.CANCEL,
    });

    buttons.push({
      action: () => this.redirectToTimeline(id),
      title: 'tws.file-uploader-modal.confirm',
      type: ModalButtonType.SUCCESS,
    });

    const dialogModal: BaseModalProps = {
      dialogDescription: MessageService.translate('text.reset items-want-to-continue'),
      dockButtons: buttons,
      focusOnOpen: true,
      id: 'dialog-modal-service',
      title: 'text.message-alert-attention',
    };

    ModalService.openDialog(dialogModal);
  }

  private redirectToTimeline(id: string): void {
    ModalService.close(id);
    ModalService.close('dialog-modal-service');
  }
}